/**
 * ProposalFollowUpModal, the branded follow-up nudge for a proposal that has
 * gone quiet. Unlike Share Proposal (which composes a message and hands it to
 * the sender's own WhatsApp / SMS / mail client), this one is sent by the
 * platform itself through pos-proposal-followup-send.
 *
 * The sender identity is resolved server-side, so the modal shows what will
 * actually go out rather than guessing: the person who sent the proposal when
 * they have their own Peace of Sleep address, otherwise the default sender
 * with replies routed back to them.
 *
 * The WhatsApp follow-up is the other way round: it opens the sender's own
 * WhatsApp pre-filled, and "Mark as sent" records it on the proposal.
 */

import { useEffect, useState } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import Modal from '../../../../components/Modal';
import { useToast } from '../../../../components/Toast';
import { useConfig } from '../../../../contexts/ConfigContext';
import { formatPhoneE164, getWhatsAppDeepLink } from '../../../../lib/messaging';
import { formatDateTime } from '../../../../utils/formatters';

interface FollowUpProposal {
  id: string;
  reference_code?: string;
  property_name?: string;
  contact_name?: string;
  contact_email?: string;
  contact_phone?: string;
  viewed_at?: string | null;
}

interface Props {
  proposal: FollowUpProposal;
  supabase: SupabaseClient;
  onClose: () => void;
  onSent?: () => void | Promise<void>;
  /** When a WhatsApp follow-up was last marked as sent, if ever. */
  whatsAppSentAt?: string | null;
}

interface PreviewResult {
  html: string;
  subject: string;
  from: string;
  replyTo: string;
  alreadySentAt: string | null;
}

/** Charlie's WhatsApp follow-up copy, used word for word. */
function whatsAppFollowUpText(name: string, property: string): string {
  return `Hi ${name}, Charlie here from Peace of Sleep™. A quick follow-up to my earlier note: since Getaway covered the launch, we've been inundated with requests, and onboarding is now scheduled in order of enquiry date. One in three guests lives with allergy sensitivity and spends half their stay in the bed, so with the new season approaching, now is the time to get ahead of it. To secure your place, reply with "POS - ${property}" and I'll call you back.`;
}

export default function ProposalFollowUpModal({ proposal, supabase, onClose, onSent, whatsAppSentAt }: Props) {
  const { showToast } = useToast();
  const [preview, setPreview] = useState<PreviewResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [testTo, setTestTo] = useState('');
  const [testing, setTesting] = useState(false);

  const recipient = (proposal.contact_email || '').trim();
  const propertyName = proposal.property_name || 'this property';

  const { countryConfig } = useConfig();
  const phonePrefix = (countryConfig as unknown as Record<string, string>)?.phone_prefix;
  const whatsAppPhone = formatPhoneE164(proposal.contact_phone, phonePrefix);
  const contactFirst = (proposal.contact_name || '').trim().split(/\s+/)[0] || 'there';
  const [whatsAppText, setWhatsAppText] = useState(
    () => whatsAppFollowUpText(contactFirst, proposal.property_name || 'your property'),
  );
  const [markingWhatsApp, setMarkingWhatsApp] = useState(false);
  const [whatsAppMarked, setWhatsAppMarked] = useState(false);

  // Build the real email server-side on open, so what is on screen is exactly
  // what the customer would receive, sender line included.
  useEffect(() => {
    let active = true;
    void (async () => {
      setLoading(true);
      try {
        const { data, error: invokeError } = await supabase.functions.invoke('pos-proposal-followup-send', {
          body: { proposal_id: proposal.id, preview_only: true },
        });
        const html = (data as { preview_html?: string })?.preview_html;
        if (invokeError || !html) {
          throw new Error(invokeError?.message ?? (data as { reason?: string })?.reason ?? 'Preview failed.');
        }
        if (!active) return;
        setPreview({
          html,
          subject: (data as { subject?: string })?.subject ?? '',
          from: (data as { from?: string })?.from ?? '',
          replyTo: (data as { reply_to?: string })?.reply_to ?? '',
          alreadySentAt: (data as { already_sent_at?: string | null })?.already_sent_at ?? null,
        });
      } catch (err: unknown) {
        if (active) setError(err instanceof Error ? err.message : 'Preview failed.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [proposal.id, supabase]);

  async function send() {
    if (!recipient) { showToast('This proposal has no contact email.', 'error'); return; }
    setSending(true);
    try {
      const { data, error: invokeError } = await supabase.functions.invoke('pos-proposal-followup-send', {
        body: { proposal_id: proposal.id, ...(preview?.alreadySentAt ? { force: true } : {}) },
      });
      if (invokeError || !(data as { success?: boolean })?.success) {
        throw new Error(invokeError?.message ?? (data as { reason?: string })?.reason ?? 'Send failed.');
      }
      setSent(true);
      showToast(`Follow-up sent to ${recipient}.`, 'success');
      if (onSent) await onSent();
    } catch (err: unknown) {
      showToast(`Follow-up failed: ${err instanceof Error ? err.message : 'unknown error'}`, 'error');
    } finally {
      setSending(false);
    }
  }

  function openWhatsApp() {
    if (!whatsAppPhone) return;
    window.open(getWhatsAppDeepLink(whatsAppPhone, whatsAppText), '_blank');
  }

  async function markWhatsAppSent() {
    setMarkingWhatsApp(true);
    try {
      const { data, error: invokeError } = await supabase.functions.invoke('pos-proposal-followup-send', {
        body: { proposal_id: proposal.id, mark_whatsapp: true },
      });
      if (invokeError || !(data as { success?: boolean })?.success) {
        throw new Error(invokeError?.message ?? (data as { reason?: string })?.reason ?? 'Could not record it.');
      }
      setWhatsAppMarked(true);
      showToast('WhatsApp follow-up recorded.', 'success');
      if (onSent) await onSent();
    } catch (err: unknown) {
      showToast(`Not recorded: ${err instanceof Error ? err.message : 'unknown error'}`, 'error');
    } finally {
      setMarkingWhatsApp(false);
    }
  }

  async function sendTest() {
    const addr = testTo.trim();
    if (!addr) { showToast('Add an address for the test.', 'error'); return; }
    setTesting(true);
    try {
      const { data, error: invokeError } = await supabase.functions.invoke('pos-proposal-followup-send', {
        body: { proposal_id: proposal.id, test_to: addr },
      });
      if (invokeError || !(data as { success?: boolean })?.success) {
        throw new Error(invokeError?.message ?? (data as { reason?: string })?.reason ?? 'Test send failed.');
      }
      showToast(`Test sent to ${addr}.`, 'success');
    } catch (err: unknown) {
      showToast(`Test failed: ${err instanceof Error ? err.message : 'unknown error'}`, 'error');
    } finally {
      setTesting(false);
    }
  }

  return (
    <Modal
      title={`Follow up: ${propertyName}`}
      size="md"
      onClose={onClose}
      footer={<button type="button" className="btn btn-secondary" onClick={onClose} disabled={sending}>Close</button>}
    >
      <div className="form-section">
        <h3 className="modal-section-heading">What goes out</h3>

        {preview?.alreadySentAt && (
          <div className="alert alert-warning mb-md">
            A follow-up already went out on {formatDateTime(preview.alreadySentAt)}. Send again only if you mean to.
          </div>
        )}
        {!proposal.viewed_at && (
          <p className="text-muted fs-xs mb-md">
            {proposal.contact_name || 'The contact'} has never opened the assessment, so the link in this email is
            the first time they will see it.
          </p>
        )}

        <div className="form-group">
          <label className="form-label">To</label>
          <input type="email" className="form-input" value={recipient} readOnly />
          {!recipient && (
            <p className="text-muted fs-xs mt-xs">
              No contact email on this proposal, so it cannot be sent. Add one on the proposal first.
            </p>
          )}
        </div>

        <div className="form-group">
          <label className="form-label">From</label>
          <input type="text" className="form-input" value={preview?.from || ''} readOnly />
          <p className="text-muted fs-xs mt-xs">
            {preview?.replyTo
              ? `Replies go to ${preview.replyTo}. It comes from whoever sent the proposal when they have their own Peace of Sleep address, otherwise from the default sender with replies routed back to them.`
              : 'Resolved when the preview loads.'}
          </p>
        </div>

        <div className="form-group">
          <label className="form-label">Subject</label>
          <input type="text" className="form-input" value={preview?.subject || ''} readOnly />
        </div>

        {loading && <p className="text-muted text-sm">Building the email…</p>}
        {error && <div className="alert alert-error mb-md">{error}</div>}
        {preview && (
          <div className="preview-container mt-sm">
            <iframe srcDoc={preview.html} className="w-full border-none cl-preview-iframe" title="Follow-up email preview" />
          </div>
        )}

        <button
          type="button"
          className="btn btn-primary btn-pos-gold w-full mt-sm"
          onClick={() => void send()}
          disabled={sending || sent || loading || !recipient || !!error}
        >
          {sending ? 'Sending…' : sent ? '✓ Follow-up sent' : preview?.alreadySentAt ? 'Send again anyway' : 'Send follow-up'}
        </button>
        <p className="text-muted fs-xs mt-xs">
          To change this copy for everyone, use Email, System, then Proposal follow-up. It applies from the next send.
        </p>
      </div>

      <div className="form-section">
        <h3 className="modal-section-heading">WhatsApp follow-up</h3>
        {whatsAppPhone ? (
          <>
            {whatsAppSentAt && !whatsAppMarked && (
              <div className="alert alert-warning mb-md">
                A WhatsApp follow-up was already marked as sent on {formatDateTime(whatsAppSentAt)}. Send again only if you mean to.
              </div>
            )}
            <div className="form-group">
              <label className="form-label">Message</label>
              <textarea
                className="form-input"
                value={whatsAppText}
                onChange={(e) => setWhatsAppText(e.target.value)}
                rows={6}
              />
            </div>
            <div className="btn-grid">
              <button type="button" className="btn btn-sm btn-secondary" onClick={openWhatsApp}>
                Open WhatsApp
              </button>
              <button
                type="button"
                className="btn btn-sm btn-primary"
                onClick={() => void markWhatsAppSent()}
                disabled={markingWhatsApp || whatsAppMarked}
              >
                {markingWhatsApp ? 'Saving…' : whatsAppMarked ? '✓ Marked as sent' : whatsAppSentAt ? 'Mark as sent again' : 'Mark as sent'}
              </button>
            </div>
            <p className="text-muted fs-xs mt-xs">
              Opens your own WhatsApp with the message filled in. Click Mark as sent once it has gone.
            </p>
          </>
        ) : (
          <p className="text-muted fs-xs">No mobile number on this proposal, so WhatsApp is off.</p>
        )}
      </div>

      <div className="form-section">
        <h3 className="modal-section-heading">Send yourself a test (optional)</h3>
        <p className="text-muted fs-xs mb-md">
          Goes to the address below instead of the customer, and is not recorded against the proposal.
        </p>
        <div className="form-group">
          <label className="form-label">Test address</label>
          <input
            type="email"
            className="form-input"
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
            placeholder="name@cleanmybed.com"
          />
        </div>
        <button
          type="button"
          className="btn btn-secondary w-full"
          onClick={() => void sendTest()}
          disabled={testing || loading || !testTo.trim()}
        >
          {testing ? 'Sending test…' : 'Send test'}
        </button>
      </div>
    </Modal>
  );
}
